import { definePreset } from '@primeuix/themes';
// PrimeNG 22 recalibrated its rem-based sizing from a 14px to a 16px root font size.
// The compat preset keeps the 14px calibration, so components render exactly as they
// did on v21. Supported until June 2027; switching to the plain aura preset needs a
// visual pass over every screen.
import AuraCompat from '@primeuix/themes/aura-compat';

export const tacsPreset = definePreset(AuraCompat, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '1px',
      sm: '2px',
      md: '3px',
      lg: '4px',
      xl: '6px',
    },
    emerald: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
      950: '#022c22',
    },
    green: {
      50: '#f0f7f3',
      100: '#daeee0',
      200: '#c4e4cd',
      300: '#b4d8bd',
      400: '#8fc99e',
      500: '#6aba7f',
      600: '#559e66',
      700: '#40824d',
      800: '#2b6634',
      900: '#164a1b',
      950: '#0b2e0d',
    },
    lime: {
      50: '#f7fee7',
      100: '#ecfccb',
      200: '#d9f99d',
      300: '#bef264',
      400: '#a3e635',
      500: '#84cc16',
      600: '#65a30d',
      700: '#4d7c0f',
      800: '#3f6212',
      900: '#365314',
      950: '#1a2e05',
    },
    red: {
      50: '#fef2f3',
      100: '#fcd9dd',
      200: '#fac0c7',
      300: '#f8a7b1',
      400: '#f68e9b',
      500: '#e6003c',
      600: '#b24752',
      700: '#86353d',
      800: '#592329',
      900: '#2d1214',
      950: '#16090a',
    },
    orange: {
      50: '#fefaf4',
      100: '#f8e8ca',
      200: '#f3d5a0',
      300: '#eec375',
      400: '#e8b04b',
      500: '#e39e21',
      600: '#c1861c',
      700: '#9f6f17',
      800: '#7d5712',
      900: '#5b3f0d',
      950: '#392808',
    },
    amber: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#451a03',
    },
    yellow: {
      50: '#fefbf2',
      100: '#fcf3d9',
      200: '#faebc0',
      300: '#f8e3a7',
      400: '#f6db8e',
      500: '#ffcd0a',
      600: '#bea657',
      700: '#8f7d41',
      800: '#5f532c',
      900: '#302a16',
      950: '#18150b',
    },
    teal: {
      50: '#f2fcfd',
      100: '#c2f2f3',
      200: '#91e7ea',
      300: '#61dce1',
      400: '#30d2d7',
      500: '#00c7ce',
      600: '#00a9af',
      700: '#008b90',
      800: '#006d71',
      900: '#005052',
      950: '#003234',
    },
    cyan: {
      50: '#f2fbff',
      100: '#c2eefe',
      200: '#91e0fe',
      300: '#61d2fd',
      400: '#30c5fd',
      500: '#00b7fc',
      600: '#009cd6',
      700: '#0080b0',
      800: '#00658b',
      900: '#004965',
      950: '#002e3f',
    },
    sky: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
      950: '#082f49',
    },
    blue: {
      50: '#eef7fb',
      100: '#d4ebf5',
      200: '#badfef',
      300: '#a0d3e9',
      400: '#86c7e3',
      500: '#008cd2',
      600: '#3d80a0',
      700: '#2e6079',
      800: '#1f4052',
      900: '#10202b',
      950: '#081015',
    },
    indigo: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
      950: '#1e1b4b',
    },
    violet: {
      50: '#f5f3ff',
      100: '#ede9fe',
      200: '#ddd6fe',
      300: '#c4b5fd',
      400: '#a78bfa',
      500: '#8b5cf6',
      600: '#7c3aed',
      700: '#6d28d9',
      800: '#5b21b6',
      900: '#4c1d95',
      950: '#2e1065',
    },
    purple: {
      50: '#faf4fb',
      100: '#e8cbec',
      200: '#d5a2dd',
      300: '#c379ce',
      400: '#b050bf',
      500: '#9e27b0',
      600: '#862196',
      700: '#6f1b7b',
      800: '#571561',
      900: '#3f1046',
      950: '#280a2c',
    },
    fuchsia: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f5d0fe',
      300: '#f0abfc',
      400: '#e879f9',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
      800: '#86198f',
      900: '#701a75',
      950: '#4a044e',
    },
    pink: {
      50: '#fef6fa',
      100: '#fad5e6',
      200: '#f6b3d1',
      300: '#f191bd',
      400: '#ed70a9',
      500: '#e94e95',
      600: '#c6427f',
      700: '#a33768',
      800: '#802b52',
      900: '#5d1f3c',
      950: '#3a1425',
    },
    rose: {
      50: '#fff1f2',
      100: '#ffe4e6',
      200: '#fecdd3',
      300: '#fda4af',
      400: '#fb7185',
      500: '#f43f5e',
      600: '#e11d48',
      700: '#be123c',
      800: '#9f1239',
      900: '#881337',
      950: '#4c0519',
    },
    slate: {
      50: '#f4f7f7',
      100: '#edf2f2',
      200: '#e6ecec',
      300: '#bdc8c9',
      400: '#94a3a5',
      500: '#6b7f81',
      600: '#556567',
      700: '#404b4d',
      800: '#2a3233',
      900: '#151819',
      950: '#0a0c0d',
    },
    gray: {
      50: '#f4f7f7',
      100: '#edf2f2',
      200: '#e6ecec',
      300: '#bdc8c9',
      400: '#94a3a5',
      500: '#6b7f81',
      600: '#556567',
      700: '#404b4d',
      800: '#2a3233',
      900: '#151819',
      950: '#0a0c0d',
    },
    zinc: {
      50: '#fafafa',
      100: '#f4f4f5',
      200: '#e4e4e7',
      300: '#d4d4d8',
      400: '#a1a1aa',
      500: '#71717a',
      600: '#52525b',
      700: '#3f3f46',
      800: '#27272a',
      900: '#18181b',
      950: '#09090b',
    },
    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
      950: '#0a0a0a',
    },
    stone: {
      50: '#fafaf9',
      100: '#f5f5f4',
      200: '#e7e5e4',
      300: '#d6d3d1',
      400: '#a8a29e',
      500: '#78716c',
      600: '#57534e',
      700: '#44403c',
      800: '#292524',
      900: '#1c1917',
      950: '#0c0a09',
    },
  },
  semantic: {
    // AuraCompat pins line-height, font-size and font-weight on components (e.g. inputs);
    // v21 inherited them from the surrounding text.
    typography: {
      lineHeight: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
    },
    transitionDuration: '0.2s',
    focusRing: {
      width: '1px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none',
    },
    disabledOpacity: '0.6',
    iconSize: '1rem',
    anchorGutter: '2px',
    primary: {
      50: '#e6f2f3',
      100: '#b3d9dc',
      200: '#80bfc4',
      300: '#4da6ad',
      400: '#007280',
      500: '#005c66',
      600: '#004d56',
      700: '#003e45',
      800: '#002e35',
      900: '#001f24',
      950: '#000f12',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.625rem',
        paddingY: '0.375rem',
      },
      lg: {
        fontSize: '1.125rem',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
      borderRadius: '{border.radius.md}',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none',
      },
      transitionDuration: '{transition.duration}',
    },
    list: {
      padding: '0.25rem 0.25rem',
      gap: '2px',
      header: {
        padding: '0.5rem 1rem 0.25rem 1rem',
      },
      option: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
        transitionDuration: '{transition.duration}',
      },
      optionGroup: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
    },
    content: {
      borderRadius: '{border.radius.md}',
    },
    mask: {
      transitionDuration: '0.15s',
    },
    navigation: {
      list: {
        padding: '0.25rem 0.25rem',
        gap: '2px',
      },
      item: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
        gap: '0.5rem',
        transitionDuration: '{transition.duration}',
      },
      submenuLabel: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
      submenuIcon: {
        size: '0.875rem',
      },
    },
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '0.75rem',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      modal: {
        borderRadius: '{border.radius.xl}',
        padding: '1.25rem',
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      },
      navigation: {
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        highlight: {
          background: '{green.300}',
          focusBackground: '{green.400}',
          color: '{primary.950}',
          focusColor: '{primary.500}',
        },
        mask: {
          background: 'rgba(0,0,0,0.4)',
          color: '{surface.200}',
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.200}',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.50}',
          filledFocusBackground: '{surface.50}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.400}',
          color: '{primary.950}',
          disabledColor: '{surface.500}',
          placeholderColor: '{surface.500}',
          invalidPlaceholderColor: '{red.600}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.600}',
          floatLabelActiveColor: '{surface.500}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.400}',
          shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
        },
        text: {
          color: '{primary.950}',
          hoverColor: '{primary.500}',
          mutedColor: '{primary.700}',
          hoverMutedColor: '{primary.700}',
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}',
          },
          popover: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}',
          },
          modal: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}',
          },
        },
        list: {
          option: {
            focusBackground: '{surface.200}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
        },
        navigation: {
          item: {
            focusBackground: '{surface.100}',
            activeBackground: '{surface.100}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}',
              activeColor: '{surface.500}',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
          submenuIcon: {
            color: '{surface.400}',
            focusColor: '{surface.500}',
            activeColor: '{surface.500}',
          },
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}',
        },
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
        mask: {
          background: 'rgba(0,0,0,0.6)',
          color: '{surface.200}',
        },
        formField: {
          background: '{surface.950}',
          disabledBackground: '{surface.700}',
          filledBackground: '{surface.800}',
          filledHoverBackground: '{surface.800}',
          filledFocusBackground: '{surface.800}',
          borderColor: '{surface.600}',
          hoverBorderColor: '{surface.500}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.300}',
          color: '{surface.0}',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{red.400}',
          floatLabelColor: '{surface.400}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.400}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.400}',
          shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
        },
        text: {
          color: '{surface.0}',
          hoverColor: '{surface.0}',
          mutedColor: '{surface.400}',
          hoverMutedColor: '{surface.300}',
        },
        content: {
          background: '{surface.900}',
          hoverBackground: '{surface.800}',
          borderColor: '{surface.700}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        overlay: {
          select: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}',
          },
          popover: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}',
          },
          modal: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}',
          },
        },
        list: {
          option: {
            focusBackground: '{surface.800}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.500}',
              focusColor: '{surface.400}',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
        },
        navigation: {
          item: {
            focusBackground: '{surface.800}',
            activeBackground: '{surface.800}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.500}',
              focusColor: '{surface.400}',
              activeColor: '{surface.400}',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
          submenuIcon: {
            color: '{surface.500}',
            focusColor: '{surface.400}',
            activeColor: '{surface.400}',
          },
        },
      },
    },
  },
  components: {
    datatable: {
      headerCell: {
        selectedBackground: '{highlight.background}',
        selectedColor: '{highlight.color}',
      },
    },
    tooltip: {
      root: {
        fontSize: 'inherit',
      },
    },
    toast: {
      root: {
        blur: '1.5px',
      },
      colorScheme: {
        light: {
          success: {
            color: '{primary.500}',
          },
        },
      },
    },
    message: {
      colorScheme: {
        light: {
          info: {
            color: '{primary.950}',
            background: '{green.100}',
            borderColor: '{green.100}',
          },
          success: {
            color: '{primary.950}',
            background: '{green.100}',
            borderColor: '{green.100}',
          },
        },
      },
    },
    datepicker: {
      colorScheme: {
        light: {
          dropdown: {
            background: '{primary.500}',
            hoverBackground: '{primary.800}',
            activeBackground: '{primary.500}',
            borderColor: '{primary.500}',
            hoverBorderColor: '{primary.800}',
            activeBorderColor: '{primary.500}',
            color: '{green.50}',
            hoverColor: '{green.50}',
            activeColor: '{green.50}',
          },
        },
      },
    },
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.500}',
              hoverBackground: '{primary.400}',
              activeBackground: '{primary.400}',
              borderColor: '{primary.500}',
              hoverBorderColor: '{primary.400}',
              activeBorderColor: '{primary.400}',
              color: '{green.100}',
              hoverColor: '{green.100}',
              activeColor: '{green.100}',
              focusRing: {
                color: '{green.400}',
                shadow: 'none',
              },
            },
          },
          outlined: {
            primary: {
              borderColor: '{primary.500}',
            },
          },
        },
      },
    },
    inputgroup: {
      addon: {
        minWidth: 'auto',
      },
    },
  },
});
